import * as React from 'react';
import { useForm } from 'react-hook-form';
import { Button } from '@/shadcn/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/shadcn/components/ui/dialog';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/shadcn/components/ui/form';
import { Input } from '@/shadcn/components/ui/input';
import { CTAButtonConfig } from './CTAButton';

export interface CTAButtonConfigModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (config: CTAButtonConfig) => void;
  initialConfig?: Partial<CTAButtonConfig>;
}

const DEFAULT_CONFIG: CTAButtonConfig = {
  text: 'Click Here',
  url: '',
  backgroundColor: '#3B82F6',
  textColor: '#FFFFFF',
  alignment: 'center',
};

export const CTAButtonConfigModal: React.FC<CTAButtonConfigModalProps> = ({
  isOpen,
  onClose,
  onSave,
  initialConfig,
}) => {
  const form = useForm<CTAButtonConfig>({
    defaultValues: {
      ...DEFAULT_CONFIG,
      ...initialConfig,
    },
  });

  // Reset form values when modal opens with new initialConfig
  // Use a ref to track if modal was previously open to avoid unnecessary resets
  const wasOpenRef = React.useRef(false);

  React.useEffect(() => {
    // Only reset when transitioning from closed to open
    if (isOpen && !wasOpenRef.current) {
      const resetValues = {
        ...DEFAULT_CONFIG,
        ...initialConfig,
      };
      form.reset(resetValues);
      // Also update preview values immediately
      setPreviewValues({
        text: resetValues.text,
        alignment: resetValues.alignment,
        backgroundColor: resetValues.backgroundColor,
        textColor: resetValues.textColor,
      });
    }
    wasOpenRef.current = isOpen;
  }, [isOpen, initialConfig, form]);

  const handleSubmit = (data: CTAButtonConfig) => {
    onSave(data);
    onClose();
  };

  const handleCancel = () => {
    form.reset();
    onClose();
  };

  // Use form values for preview without watching to avoid re-renders
  const [previewValues, setPreviewValues] = React.useState({
    text: initialConfig?.text || DEFAULT_CONFIG.text,
    alignment: initialConfig?.alignment || DEFAULT_CONFIG.alignment,
    backgroundColor: initialConfig?.backgroundColor || DEFAULT_CONFIG.backgroundColor,
    textColor: initialConfig?.textColor || DEFAULT_CONFIG.textColor,
  });

  // Reference to the text input for focus management
  const textInputRef = React.useRef<HTMLInputElement>(null);

  // Update preview values when form changes, but with debouncing
  React.useEffect(() => {
    const subscription = form.watch((values) => {
      setPreviewValues({
        text: values.text || DEFAULT_CONFIG.text,
        alignment: values.alignment || DEFAULT_CONFIG.alignment,
        backgroundColor: values.backgroundColor || DEFAULT_CONFIG.backgroundColor,
        textColor: values.textColor || DEFAULT_CONFIG.textColor,
      });
    });

    return () => subscription.unsubscribe();
  }, [form]);

  // Focus the text input when modal opens
  React.useEffect(() => {
    if (isOpen && textInputRef.current) {
      // Use setTimeout to ensure the modal is fully rendered
      setTimeout(() => {
        textInputRef.current?.focus();
      }, 100);
    }
  }, [isOpen]);

  // Global event handler to capture all keyboard events when modal is open
  React.useEffect(() => {
    if (!isOpen) return;

    const handleGlobalKeyDown = (e: KeyboardEvent) => {
      // Only allow events from elements inside our modal
      const modalElement = document.querySelector('[data-cta-modal="true"]');
      if (modalElement && !modalElement.contains(e.target as Node)) {
        e.stopPropagation();
        e.stopImmediatePropagation();
        e.preventDefault();
      }
    };

    const handleGlobalKeyUp = (e: KeyboardEvent) => {
      // Only allow events from elements inside our modal
      const modalElement = document.querySelector('[data-cta-modal="true"]');
      if (modalElement && !modalElement.contains(e.target as Node)) {
        e.stopPropagation();
        e.stopImmediatePropagation();
        e.preventDefault();
      }
    };

    // Capture events before they reach Draft.js
    document.addEventListener('keydown', handleGlobalKeyDown, { capture: true });
    document.addEventListener('keyup', handleGlobalKeyUp, { capture: true });

    return () => {
      document.removeEventListener('keydown', handleGlobalKeyDown, { capture: true });
      document.removeEventListener('keyup', handleGlobalKeyUp, { capture: true });
    };
  }, [isOpen]);

  return (
    <>
      {/* Backdrop to prevent interaction with background */}
      {isOpen && (
        <div
          className="fixed inset-0 bg-black/50 z-[999998]"
          style={{ zIndex: 999998 }}
          onKeyDown={(e) => e.stopPropagation()}
          onKeyUp={(e) => e.stopPropagation()}
          onKeyPress={(e) => e.stopPropagation()}
        />
      )}
      <Dialog open={isOpen} onOpenChange={(open) => !open && handleCancel()} modal>
        <DialogContent
          data-cta-modal="true"
          className="sm:max-w-md z-[999999] p-4"
          style={{ zIndex: 999999 }}
          onKeyDown={(e) => {
          // Prevent keydown events from bubbling to parent components
          e.stopPropagation();
        }}
          onKeyUp={(e) => {
          // Prevent keyup events from bubbling to parent components
          e.stopPropagation();
        }}
          onKeyPress={(e) => {
          // Prevent keypress events from bubbling to parent components
          e.stopPropagation();
        }}
          onInteractOutside={(e) => {
          // Prevent closing when clicking on form elements
          e.preventDefault();
        }}
          onPointerDownOutside={(e) => {
          // Prevent closing and background interaction
          e.preventDefault();
        }}
          onEscapeKeyDown={(e) => {
          // Allow ESC to close, but stop propagation
          e.stopPropagation();
          handleCancel();
        }}
        >
          <DialogHeader>
            <DialogTitle>Configure CTA Button</DialogTitle>
          </DialogHeader>

          <Form {...form}>
            <form onSubmit={form.handleSubmit(handleSubmit)} className="space-y-4">
              <FormField
                control={form.control}
                name="text"
                rules={{
                required: 'Button text is required',
                minLength: {
                  value: 1,
                  message: 'Button text must not be empty',
                },
              }}
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Button Text</FormLabel>
                    <FormControl>
                      <Input
                        ref={textInputRef}
                        placeholder="Enter button text"
                        {...field}
                        onKeyDown={(e) => {
                        // Stop propagation to prevent events from reaching the background editor
                        e.stopPropagation();
                      }}
                        onKeyUp={(e) => {
                        // Stop propagation to prevent events from reaching the background editor
                        e.stopPropagation();
                      }}
                        onKeyPress={(e) => {
                        // Stop propagation to prevent events from reaching the background editor
                        e.stopPropagation();
                      }}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
              )}
              />

              <FormField
                control={form.control}
                name="url"
                rules={{
                required: 'URL is required',
                pattern: {
                  value: /^https?:\/\/.+/,
                  message: 'Please enter a valid URL starting with http:// or https://',
                },
              }}
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Link URL</FormLabel>
                    <FormControl>
                      <Input
                        placeholder="https://example.com"
                        {...field}
                        onKeyDown={(e) => e.stopPropagation()}
                        onKeyUp={(e) => e.stopPropagation()}
                        onKeyPress={(e) => e.stopPropagation()}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
              )}
              />

              <FormField
                control={form.control}
                name="alignment"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Button Alignment</FormLabel>
                    <FormControl>
                      <select
                        {...field}
                        className="flex h-8 w-full items-center justify-between whitespace-nowrap rounded-lg border border-input bg-transparent px-3 py-2 text-sm shadow-none ring-offset-background focus:outline-none focus:ring-1 focus:ring-ring disabled:cursor-not-allowed disabled:opacity-50 hover:border-grey-4"
                      >
                        <option value="left">Left</option>
                        <option value="center">Center</option>
                        <option value="right">Right</option>
                      </select>
                    </FormControl>
                    <FormMessage />
                  </FormItem>
              )}
              />

              <div className="grid grid-cols-2 gap-4">
                <FormField
                  control={form.control}
                  name="backgroundColor"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Background Color</FormLabel>
                      <FormControl>
                        <div className="flex items-center space-x-2">
                          <Input
                            type="color"
                            className="w-12 h-8 p-1 border rounded"
                            {...field}
                            onKeyDown={(e) => e.stopPropagation()}
                            onKeyUp={(e) => e.stopPropagation()}
                            onKeyPress={(e) => e.stopPropagation()}
                          />
                          <Input
                            placeholder="#3B82F6"
                            {...field}
                            onKeyDown={(e) => e.stopPropagation()}
                            onKeyUp={(e) => e.stopPropagation()}
                            onKeyPress={(e) => e.stopPropagation()}
                          />
                        </div>
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                )}
                />

                <FormField
                  control={form.control}
                  name="textColor"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Text Color</FormLabel>
                      <FormControl>
                        <div className="flex items-center space-x-2">
                          <Input
                            type="color"
                            className="w-12 h-8 p-1 border rounded"
                            {...field}
                            onKeyDown={(e) => e.stopPropagation()}
                            onKeyUp={(e) => e.stopPropagation()}
                            onKeyPress={(e) => e.stopPropagation()}
                          />
                          <Input
                            placeholder="#FFFFFF"
                            {...field}
                            onKeyDown={(e) => e.stopPropagation()}
                            onKeyUp={(e) => e.stopPropagation()}
                            onKeyPress={(e) => e.stopPropagation()}
                          />
                        </div>
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                )}
                />
              </div>

              {/* Preview */}
              <div className="pt-4 border-t">
                <FormLabel className="text-sm font-medium text-gray-700 mb-2 block">
                  Preview
                </FormLabel>
                <div
                  className={`flex ${
                  previewValues.alignment === 'left'
                    ? 'justify-start'
                    : previewValues.alignment === 'right'
                    ? 'justify-end'
                    : 'justify-center'
                }`}
                >
                  <button
                    type="button"
                    className="inline-flex items-center justify-center px-6 py-3 rounded-lg font-medium text-sm transition-all duration-200"
                    style={{
                    backgroundColor: previewValues.backgroundColor,
                    color: previewValues.textColor,
                  }}
                    disabled
                  >
                    {previewValues.text || 'Button Text'}
                  </button>
                </div>
              </div>

              <DialogFooter className="flex gap-2">
                <Button
                  type="button"
                  variant="outline"
                  onClick={handleCancel}
                >
                  Cancel
                </Button>
                <Button type="submit">
                  Save Button
                </Button>
              </DialogFooter>
            </form>
          </Form>
        </DialogContent>
      </Dialog>
    </>
  );
};

CTAButtonConfigModal.displayName = 'CTAButtonConfigModal';
